import { create } from 'zustand';

/**
 * The Actions panel's own DOM node, published while the panel is open.
 *
 * A consumer that must stay mounted globally (for reasons FlowPanels has no
 * visibility into — e.g. URL-param handling that needs to survive route
 * changes) portals its content into this node instead of being passed
 * through the `actions` prop, which unmounts with the panel. `null` while
 * the panel is closed, and whenever no `FlowPanels` is mounted at all
 * (every non-`/flow/*` route).
 *
 * ── Why a zustand store, not a React context ─────────────────────────────
 * The consumer this exists for — `shell-next/rail/RailDock.tsx` — is mounted
 * in `AppRouter.tsx` as a SIBLING of `<Outlet />`, while `FlowPanels` lives
 * under `FlowShell`, inside that Outlet. A context provider rendered by
 * `FlowPanels` can only reach its own React descendants, so the dock could
 * never read it. CLAUDE.md ("Zustand for Module-Scope Client State") makes
 * zustand the required form; plain `create<State>(() => ...)`, no
 * middleware. State is data-only so `setState(initialState, true)` (the
 * test-reset contract) can never strip the writer, which is a module function.
 */
export interface FlowPanelsSlotsState {
  actionsNode: HTMLElement | null;
}

/** Exported per the CLAUDE.md zustand convention — tests reset with `setState(initialState, true)`. */
export const initialState: FlowPanelsSlotsState = { actionsNode: null };

export const useFlowPanelsSlotsStore = create<FlowPanelsSlotsState>(() => ({ ...initialState }));

/**
 * `FlowPanels` passes this as the Actions panel div's callback ref. React
 * calls it with the element on mount and `null` on unmount (panel closed OR
 * `FlowPanels` itself leaving the tree), so the store tracks the panel's
 * lifecycle with no effect of its own. Stable identity (a module function,
 * not an inline arrow), so React does not re-invoke it on every render.
 */
export function setFlowPanelsActionsNode(node: HTMLElement | null): void {
  useFlowPanelsSlotsStore.setState({ actionsNode: node });
}

export function useFlowPanelsActionsNode(): HTMLElement | null {
  return useFlowPanelsSlotsStore((s) => s.actionsNode);
}
